import { LASHING_BRIDGE_MIN_DOUBLE_GAP_IN_MM, LASHING_BRIDGE_MIN_GAP_IN_MM } from "../consts";
import { BayLevelEnum } from "open-vessel-definition";
import { preCalculateCurrentVesselParts } from "../preCalculateCurrentVesselParts";
const slotsCache = new WeakMap();
/**
 * Places where a lashing bridge can stand, from bow to stern. A block of bays is a 40' pair or
 * a single bay. Between two blocks, the bridge is written as FWD of the stern block's fwd bay:
 * - every block offers its FWD;
 * - a block offers its AFT only when two bridges fit before the next block (a vessel part
 *   follows it, the gap is at least `minDoubleGapInMm`, or the gap is unknown) and on the
 *   stern-most block;
 * - never between the two 20' bays of a pair.
 * Gaps use the real or the artificial LCGs (20' and 40' slots only, as longer containers are
 * stowed above the bridges).
 */
export function getLashingBridgeSlots(p, { minDoubleGapInMm = LASHING_BRIDGE_MIN_DOUBLE_GAP_IN_MM } = {}) {
    var _a;
    const useCache = minDoubleGapInMm === LASHING_BRIDGE_MIN_DOUBLE_GAP_IN_MM;
    const cached = useCache ? slotsCache.get(p) : undefined;
    if (cached)
        return cached;
    const bays = Object.values(p.bayLevelPositionsDict)
        .map((blp) => blp.isoBay)
        .filter((v, i, a) => a.indexOf(v) === i)
        .sort();
    const blocks = getBlocks(p, bays);
    const sequence = preCalculateCurrentVesselParts(bays.map((isoBay) => ({ isoBay })), (_a = p.vesselPartsData) !== null && _a !== void 0 ? _a : []);
    /** A vessel part sits between the last bay of `a` and the first bay of `b` in the sequence */
    const partBetween = (a, b) => {
        const from = sequence.indexOf(a[a.length - 1]);
        const to = sequence.indexOf(b[0]);
        return (from >= 0 && to > from && sequence.slice(from + 1, to).some((s) => typeof s !== "string"));
    };
    const slots = [];
    blocks.forEach((block, i) => {
        const label = block.join("-");
        const ends = getBlockEnds(p, block);
        const prev = blocks[i - 1];
        const next = blocks[i + 1];
        // FWD of the fwd bay
        const prevEnds = prev ? getBlockEnds(p, prev) : undefined;
        const prevPart = prev ? partBetween(prev, block) : false;
        const prevGap = ends && prevEnds ? prevEnds.aft - ends.fore : undefined;
        const prevOffersAft = prev ? offersAft(prevPart, prevGap, minDoubleGapInMm) : true;
        slots.push({
            isoBay: block[0],
            side: "fore",
            block: label,
            end: ends === null || ends === void 0 ? void 0 : ends.fore,
            neighbourBlock: prev === null || prev === void 0 ? void 0 : prev.join("-"),
            neighbourEnd: prevEnds === null || prevEnds === void 0 ? void 0 : prevEnds.aft,
            gap: prevGap,
            partBetween: prevPart,
            sharedWith: prev && !prevOffersAft ? { isoBay: prev[prev.length - 1], side: "aft" } : undefined,
        });
        // AFT of the aft bay, only when two bridges fit before the next block (or at the stern)
        const nextEnds = next ? getBlockEnds(p, next) : undefined;
        const nextPart = next ? partBetween(block, next) : false;
        const nextGap = ends && nextEnds ? ends.aft - nextEnds.fore : undefined;
        if (!next || offersAft(nextPart, nextGap, minDoubleGapInMm))
            slots.push({
                isoBay: block[block.length - 1],
                side: "aft",
                block: label,
                end: ends === null || ends === void 0 ? void 0 : ends.aft,
                neighbourBlock: next === null || next === void 0 ? void 0 : next.join("-"),
                neighbourEnd: nextEnds === null || nextEnds === void 0 ? void 0 : nextEnds.fore,
                gap: nextGap,
                partBetween: nextPart,
            });
    });
    if (useCache)
        slotsCache.set(p, slots);
    return slots;
}
function offersAft(partBetween, gap, minDoubleGapInMm) {
    return partBetween || gap === undefined || gap >= minDoubleGapInMm;
}
/**
 * Median gap between neighbour blocks with no vessel part in between, at least
 * {@link LASHING_BRIDGE_MIN_GAP_IN_MM}: where bridges usually stand
 */
export function getTypicalLashingBridgeGap(slots) {
    const gaps = slots
        .filter((s) => s.side === "fore" && !s.partBetween && s.gap !== undefined)
        .map((s) => s.gap)
        .filter((g) => g >= LASHING_BRIDGE_MIN_GAP_IN_MM)
        .sort((a, b) => a - b);
    if (!gaps.length)
        return undefined;
    const mid = gaps.length / 2;
    return gaps.length % 2 ? gaps[Math.floor(mid)] : (gaps[mid - 1] + gaps[mid]) * 0.5;
}
/** Blocks of bays, bow to stern: a bay and its confirmed paired bay, or a single bay */
function getBlocks(p, bays) {
    const assigned = new Set();
    const blocks = [];
    bays.forEach((bay) => {
        var _a;
        if (assigned.has(bay))
            return;
        const paired = (_a = p.lcgsBy20Bay[bay]) === null || _a === void 0 ? void 0 : _a.confirmedPairedBay;
        const block = paired && bays.includes(paired) && !assigned.has(paired)
            ? [bay, paired].sort()
            : [bay];
        block.forEach((b) => assigned.add(b));
        blocks.push(block);
    });
    return blocks.sort((a, b) => Number(a[0]) - Number(b[0]));
}
/** Fore and aft ends of a block of bays */
function getBlockEnds(p, block) {
    const ends = block.map((b) => getBayEnds(p, b)).filter((e) => e !== undefined);
    if (!ends.length)
        return undefined;
    return {
        fore: Math.max(...ends.map((e) => e.fore)),
        aft: Math.min(...ends.map((e) => e.aft)),
    };
}
/** Fore and aft ends of a 20' bay from its 20' and 40' slots (or all its slots if it has none of those) */
export function getBayEnds(p, isoBay) {
    const lcgs20 = p.lcgsBy20Bay[isoBay];
    if (!lcgs20)
        return undefined;
    let fore = -Infinity;
    let aft = Infinity;
    lcgs20.lcgs.forEach((triplet, size) => {
        if (size > 40)
            return;
        if (triplet.foreLcg > fore)
            fore = triplet.foreLcg;
        if (triplet.aftLcg < aft)
            aft = triplet.aftLcg;
    });
    if (!Number.isFinite(fore) || !Number.isFinite(aft)) {
        fore = lcgs20.maxForeLcg;
        aft = lcgs20.minAftLcg;
    }
    return Number.isFinite(fore) && Number.isFinite(aft) ? { fore, aft } : undefined;
}
/**
 * Fore and aft ends of the containers longer than 40' that some bays stow low, with their bottom
 * less than `heightInMm` over the stack bottom of their row (above deck). They overhang the
 * 20' and 40' ends, so a lashing bridge lower than that must clear them.
 * @returns `undefined` when the bays stow no longer containers that low
 */
export function getLowLongContainerEnds(p, bays, heightInMm, tierHeightInMm) {
    let fore = -Infinity;
    let aft = Infinity;
    bays.forEach((isoBay) => {
        var _a;
        const blp = p.bayLevelPositionsDict[`${isoBay}-${BayLevelEnum.ABOVE}`];
        const lcgs = (_a = p.lcgsBy20Bay[isoBay]) === null || _a === void 0 ? void 0 : _a.lcgs;
        if (!blp || !lcgs)
            return;
        blp.cellsInfo.forEach((cell) => {
            var _a, _b;
            if (cell.restricted)
                return;
            const bottomTier = Number((_b = (_a = blp.rowBottoms.get(cell.row)) === null || _a === void 0 ? void 0 : _a.bottomTier) !== null && _b !== void 0 ? _b : blp.minTier);
            const bottom = ((Number(cell.tier) - bottomTier) / 2) * tierHeightInMm;
            if (!Number.isFinite(bottom) || bottom >= heightInMm)
                return;
            const sizes = cell.sizes ? [...cell.sizes.keys()] : cell.maxSize ? [cell.maxSize] : [];
            sizes.forEach((size) => {
                const triplet = size > 40 ? lcgs.get(size) : undefined;
                if (!triplet)
                    return;
                if (triplet.foreLcg > fore)
                    fore = triplet.foreLcg;
                if (triplet.aftLcg < aft)
                    aft = triplet.aftLcg;
            });
        });
    });
    return Number.isFinite(fore) && Number.isFinite(aft) ? { fore, aft } : undefined;
}
